import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  AssessmentDto,
  DoubleMaterialityESRSAreaEnum,
} from "@green0meter/api-client";

import AssessmentEditorActions from "./components/AssessmentEditorActions/AssessmentEditorActions";
import { validationSchema } from "./validationSchema";
import {
  ensureUniqueAssessmentId,
  generateAssessmentId,
} from "./AssessmentEditor.helpers";
import AssessmentEditorContent from "./AssessmentEditorContent";
import type { AssessmentFormValues } from "./AssessmentEditor.types";
import { useESRSTopics } from "./useESRSTopics";

import { useGetAssessments } from "@api";
import { useUserStore } from "@store/user";
import { Drawer } from "@components/common";
import { Stack, Typography } from "@components/base";
import { getYearFromSearchParams } from "@helpers";

interface AssessmentEditorProps {
  open: boolean;
  onClose: () => void;
  assessment: AssessmentDto | null;
}

const AssessmentEditor: FC<AssessmentEditorProps> = ({
  open,
  onClose,
  assessment,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const [isPendingOperation, setIsPendingOperation] = useState(false);
  const { getSubtopicsForTopic, getSubSubtopicsForTopic } = useESRSTopics();

  const subtopicLookup = (
    topicCode: number | string,
    subTopicCode: string,
  ): { index: number } | null => {
    const subtopics = getSubtopicsForTopic(topicCode);
    const index = subtopics.findIndex((st) => st.code === subTopicCode);

    return index >= 0 ? { index } : null;
  };

  const subSubtopicLookup = (
    topicCode: number | string,
    subTopicCode: string,
    subSubTopicCode: string,
  ): { index: number } | null => {
    const subSubtopics = getSubSubtopicsForTopic(topicCode, subTopicCode);
    const index = subSubtopics.findIndex((sst) => sst.code === subSubTopicCode);

    return index >= 0 ? { index } : null;
  };

  const assessmentId = generateAssessmentId(
    DoubleMaterialityESRSAreaEnum.E1,
    null,
    null,
    new Date().getFullYear(),
    [],
    undefined,
    {
      subtopicLookup,
      subSubtopicLookup,
    },
  );

  const form = useForm<AssessmentFormValues>({
    // @ts-expect-error yup schema not matching with form values
    resolver: yupResolver<AssessmentFormValues>(validationSchema),
    mode: "onChange",
    defaultValues: {
      assessmentId: assessmentId,
      year,
      topic: DoubleMaterialityESRSAreaEnum.E1,
      subTopic: null,
      subSubTopic: null,
      partOfValueChain: [],
      relevantSubjects: [],
      sustainabilityMatter: "",
      impacts: "",
      risks: "",
      opportunities: "",
      sources: "",
      attachments: [],
    },
  });

  const { control, reset, setValue, formState } = form;

  // Get all existing assessments to check for ID conflicts
  const { assessments } = useGetAssessments(user, {
    year,
  });

  // Watch the fields used for ID generation
  const watchedFields = useWatch({
    control,
    name: ["topic", "subTopic", "subSubTopic", "year"],
  });

  const [topic, subTopic, subSubTopic] = watchedFields;
  const watchedAssessmentId = useWatch({
    control,
    name: "assessmentId",
  });

  // Auto-generate assessment ID when relevant fields change (only for new assessments)
  useEffect(() => {
    if (!assessment) {
      const existingAssessments = assessments?.data || [];
      const fieldsDirty =
        !!formState.dirtyFields.topic ||
        !!formState.dirtyFields.subTopic ||
        !!formState.dirtyFields.subSubTopic ||
        !!formState.dirtyFields.year;

      if (!fieldsDirty) return;

      const topicValue = topic as DoubleMaterialityESRSAreaEnum;
      const subTopicValue = subTopic;
      const subSubTopicValue = subSubTopic;

      const generatedId = generateAssessmentId(
        topicValue,
        subTopicValue,
        subSubTopicValue,
        year as number,
        existingAssessments,
        undefined,
        {
          subtopicLookup,
          subSubtopicLookup,
        },
      );

      if (generatedId) {
        setValue("assessmentId", generatedId, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [
    topic,
    subTopic,
    subSubTopic,
    year,
    assessment,
    assessments?.data,
    setValue,
    formState.dirtyFields,
  ]);

  useEffect(() => {
    if (!watchedAssessmentId) return;
    const uniqueId = ensureUniqueAssessmentId(
      watchedAssessmentId,
      assessments?.data || [],
      assessment?.id,
    );

    if (uniqueId !== watchedAssessmentId) {
      setValue("assessmentId", uniqueId, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [watchedAssessmentId, assessments?.data, assessment?.id, setValue]);

  useEffect(() => {
    if (assessment) {
      reset({
        assessmentId: assessment.assessmentId,
        year: assessment.year,

        topic: assessment.topic,
        subTopic: assessment.subTopic ? String(assessment.subTopic) : null,
        subSubTopic: assessment.subSubTopic ? String(assessment.subSubTopic) : null,
        partOfValueChain: assessment.partOfValueChain,
        relevantSubjects: assessment.relevantSubjects?.map((c) => c.id) || [],
        sustainabilityMatter: assessment.sustainabilityMatter,
        impacts: assessment.impacts,
        risks: assessment.risks,
        opportunities: assessment.opportunities,
        sources: assessment.sources,
        attachments: assessment.attachments || [],
      });
    } else {
      // Reset to default values for create mode
      reset({
        assessmentId: generateAssessmentId(
          DoubleMaterialityESRSAreaEnum.E1,
          null,
          null,
          year,
          assessments?.data || [],
          undefined,
          {
            subtopicLookup,
            subSubtopicLookup,
          },
        ),
        year,
        topic: DoubleMaterialityESRSAreaEnum.E1,
        subTopic: null,
        subSubTopic: null,
        partOfValueChain: [],
        relevantSubjects: [],
        sustainabilityMatter: "",
        impacts: "",
        risks: "",
        opportunities: "",
        sources: "",
        attachments: [],
      });
    }
  }, [assessment, reset]);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const content = (
    <AssessmentEditorContent
      form={form}
      disabled={isPendingOperation}
      t={t}
      assessment={assessment}
    />
  );

  const contentWithHeader = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <EditIcon sx={{ color: "text.secondary" }} />
        <Typography variant="h6" color="text.primary">
          {assessment
            ? t("assessments.editAssessment", "EDIT ASSESSMENT")
            : t("assessments.addAssessment", "Add assessment")}
        </Typography>
      </Stack>
      {content}
      <AssessmentEditorActions
        handleClose={handleClose}
        form={form}
        assessment={assessment}
        onDeleteSuccess={onClose}
        onUpdateSuccess={onClose}
        onCreateSuccess={onClose}
        onPendingChange={setIsPendingOperation}
      />
    </Stack>
  );

  return (
    <Drawer open={open} onClose={handleClose} title="" content={contentWithHeader} />
  );
};

export default AssessmentEditor;
